import { useState, useEffect, useRef } from "react";

/**
 *  disableBottomContentPadding - by default it adds padding to next element that is translated
 *      to prevent over lap with next sibling
 *  runImmediately - sets translation and padding on first run
 */

const MAX_HEADER_HEIGHT_RETRY_FRAMES = 60 * 2;

export function useHideOnScrollAnimated(
	userOptions: {
		disableBottomContentPadding?: boolean;
		runImmediately?: boolean;
	} = {}
): {
	setHeaderRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
} {
	const [headerRef, setHeaderRef] = useState<HTMLElement | null>(null);
	const retryCounter = useRef<number>(0);

	//#region map user options

	const options = {
		disableBottomContentPadding: false,
		runImmediately: false,
	};

	Object.assign(options, userOptions);

	//#endregion map user options

	const { disableBottomContentPadding, runImmediately } = options;

	useEffect(() => {
		const headerNextSibling = headerRef?.nextElementSibling as HTMLElement;

		if (headerRef == null || headerNextSibling == null) {
			return;
		}

		/** Min diff to trigger hide or show for header. */
		const minDiff = 1;

		/** 0 translates by header height. Or multiply diff in scroll position by value. */
		const translateDiffMultiplayer = 0;

		/** By how much scroll position can be smaller from content to force hide header. */
		const maxScrollMargin = window.innerHeight * 0.5;

		let lastScrollPosition = window.pageYOffset;
		let lastHeight = headerNextSibling.offsetHeight;

		let working = false;
		retryCounter.current = 0;

		const updateHeaderTranslate = (): void => {
			if (working) return;

			working = true;

			if (
				headerRef.offsetHeight <= 0 &&
				retryCounter.current < MAX_HEADER_HEIGHT_RETRY_FRAMES
			) {
				retryCounter.current = retryCounter.current + 1;
				window.requestAnimationFrame(updateHeaderTranslate);
				working = false;
				return;
			}

			retryCounter.current = 0;

			// Force open header, window can not be scrolled
			if (shouldForcerOpen()) {
				translateHeader(Number.POSITIVE_INFINITY);
				working = false;
				return;
			}

			const currentScrollPosition = window.pageYOffset;
			const currentContentHeight = headerNextSibling.offsetHeight;

			const diff = lastScrollPosition - currentScrollPosition;
			const heightDiff = currentContentHeight - lastHeight;

			// Force hide header on bottom of window, this is max scroll
			if (
				currentScrollPosition + maxScrollMargin >=
				document.documentElement.scrollHeight -
					document.documentElement.clientHeight
			) {
				translateHeader(Number.NEGATIVE_INFINITY);
			} else if (
				// Required min scroll diff to trig
				Math.abs(diff) < minDiff ||
				// Content reduced in size
				heightDiff < 0
			) {
				// Do nothing
			} else {
				translateHeader(diff);
			}

			working = false;

			lastHeight = headerNextSibling.offsetHeight;
			lastScrollPosition = window.pageYOffset;
		};

		function translateHeader(diff: number): void {
			if (headerRef == null || headerNextSibling == null) {
				return;
			}

			const headerHeight = headerRef?.offsetHeight;

			let translateDiff = diff * translateDiffMultiplayer;

			// If multiplayer is 0 then translate by header height
			if (translateDiffMultiplayer === 0) {
				translateDiff = headerHeight * Math.sign(diff);
			}

			// Current amount of offset
			const headerCurrentTranslate = getTranslateXY(headerRef).translateY;
			let newTranslate = headerCurrentTranslate + translateDiff;

			if (newTranslate < -headerHeight) {
				newTranslate = -headerHeight;
			} else if (newTranslate > 0) {
				newTranslate = 0;
			}

			const nweBottomPadding = Math.abs(headerHeight + newTranslate);

			/** This will translate header up and hide it. */
			headerRef.style.transform = `translate(0, ${newTranslate}px)`;

			/** When header is not hidden translate content by shown header height down. */
			headerNextSibling.style.transform = `translate(0, ${
				headerHeight + newTranslate
			}px)`;

			if (!disableBottomContentPadding) {
				/** Add  padding to bottom of content equal to translation down to not overlap with next sibling. */
				headerNextSibling.style.paddingBottom = `${nweBottomPadding}px`;
			}

			headerNextSibling.style.opacity = "0.99";
			requestAnimationFrame(() => {
				headerNextSibling.style.opacity = "";
			});
		}

		function shouldForcerOpen(): boolean {
			if (window.scrollY <= 0) {
				// For iSmartPhone with negative scroll positions
				return true;
			}

			const windowHeight = window.innerHeight;

			const headerSelector = document.querySelector("header");
			const header =
				headerSelector == null
					? 0
					: headerSelector.getBoundingClientRect().height;

			const slipMiniSelector = document.querySelector(".slip--mini");
			const slipMini =
				slipMiniSelector == null
					? 0
					: slipMiniSelector.getBoundingClientRect().height;

			const footer =
				document.querySelector("footer")?.getBoundingClientRect()
					.height || 0;

			const visibleHeightForContents =
				windowHeight - header - slipMini - footer;

			// Don't count bottom padding, it is used to move next sibling of content when content is translated
			const contentHeight =
				headerNextSibling.clientHeight -
				Number.parseInt(headerNextSibling.style.paddingBottom || "0");
			const headerHeight = headerRef?.offsetHeight || 0;

			return visibleHeightForContents * 2 > contentHeight + headerHeight;
		}

		const onScroll = (): void => {
			window.requestAnimationFrame(updateHeaderTranslate);
		};

		if (runImmediately) {
			onScroll();
		}

		// Set initial offset for header
		translateHeader(0);
		window.addEventListener("scroll", onScroll);
		return () => window.removeEventListener("scroll", onScroll);
	}, [headerRef != null]);

	return {
		setHeaderRef,
	};
}

function getTranslateXY(element: HTMLElement): {
	translateX: number;
	translateY: number;
} {
	const style = window.getComputedStyle(element);
	const matrix = new DOMMatrixReadOnly(style.transform);
	return {
		translateX: matrix.m41,
		translateY: matrix.m42,
	};
}
